import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const LogisticsPage = () => {
  const transportModes = [
    {
      icon: 'ri-truck-line',
      title: 'Land Transport',
      description: 'Efficient road freight services across Europe and Ukraine with flexible scheduling and door-to-door delivery options.',
      features: [
        'Full truckload (FTL) and less-than-truckload (LTL)',
        'Temperature-controlled transport',
        'Express delivery options',
        'Cross-border expertise',
      ],
      image: 'https://public.readdy.ai/ai/img_res/a5ef689ad2a969ac1667fed41b7cbcd0.jpg',
    },
    {
      icon: 'ri-plane-line',
      title: 'Air Freight',
      description: 'Fast international air cargo services for time-sensitive shipments with global network coverage and priority handling.',
      features: [
        'Express and standard air freight',
        'Consolidated and direct flights',
        'Customs clearance coordination',
        'Real-time tracking',
      ],
      image: 'https://readdy.ai/api/search-image?query=cargo%20airplane%20at%20airport%20loading%20dock%20with%20freight%20containers%20professional%20air%20logistics%20operations%20modern%20aircraft%20with%20ground%20handling%20equipment%20international%20shipping%20service%20blue%20sky%20background&width=800&height=600&seq=logistics-002&orientation=landscape',
    },
    {
      icon: 'ri-ship-line',
      title: 'Sea Freight',
      description: 'Cost-effective ocean shipping solutions for large volume cargo with full container load and consolidation services.',
      features: [
        'FCL and LCL container shipping',
        'Port-to-port and door-to-door',
        'Cargo insurance options',
        'Documentation support',
      ],
      image: 'https://readdy.ai/api/search-image?query=cargo%20ship%20with%20containers%20at%20modern%20port%20terminal%20professional%20maritime%20logistics%20operations%20large%20container%20vessel%20with%20cranes%20loading%20freight%20international%20sea%20transport%20blue%20ocean%20background&width=800&height=600&seq=logistics-003&orientation=landscape',
    },
  ];

  const services = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Customs Clearance',
      description: 'Expert customs brokerage services ensuring smooth clearance of your shipments through all regulatory requirements.',
    },
    {
      icon: 'ri-route-line',
      title: 'Route Optimization',
      description: 'Strategic planning of delivery routes to minimize costs and transit times while maintaining reliability.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cargo Insurance',
      description: 'Comprehensive insurance coverage options to protect your valuable shipments throughout the journey.',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Tracking & Visibility',
      description: 'Real-time shipment tracking with detailed status updates and proactive communication.',
    },
    {
      icon: 'ri-box-3-line',
      title: 'Warehousing',
      description: 'Secure storage facilities with inventory management and distribution services across key locations.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support',
      description: 'Professional logistics coordinators available to assist with all aspects of your shipment.',
    },
  ];

  const regions = [
    { name: 'Ukraine', cities: ['Kyiv', 'Lviv', 'Odesa', 'Kharkiv', 'Dnipro'] },
    { name: 'Poland', cities: ['Warsaw', 'Kraków', 'Gdańsk', 'Wrocław'] },
    { name: 'Germany', cities: ['Berlin', 'Hamburg', 'Munich', 'Frankfurt'] },
    { name: 'Netherlands', cities: ['Amsterdam', 'Rotterdam', 'Utrecht'] },
    { name: 'Czech Republic', cities: ['Prague', 'Brno', 'Ostrava'] },
    { name: 'Austria', cities: ['Vienna', 'Graz', 'Linz'] },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative pt-32 pb-24 px-6 lg:px-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
            <i className="ri-global-line text-white"></i>
            <span className="text-sm text-white font-semibold">International Logistics</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Global <strong>Logistics Solutions</strong>
          </h1>

          <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Comprehensive cargo coordination and international shipping services with multi-modal transport options and full customs support.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
              <i className="ri-truck-line text-blue-600"></i>
              <span className="text-sm text-blue-600 font-semibold">Transport Modes</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Multi-Modal <strong>Transport Options</strong>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Choose the optimal shipping method for your cargo based on urgency, volume, and destination.
            </p>
          </div>

          <div className="space-y-24">
            {transportModes.map((mode, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <i className={`${mode.icon} text-white text-4xl`}></i>
                  </div>

                  <h3 className="text-3xl font-bold text-slate-900 mb-4">{mode.title}</h3>
                  <p className="text-lg text-slate-600 mb-8 leading-relaxed">{mode.description}</p>

                  <div className="space-y-3">
                    {mode.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <span className="text-slate-700 text-lg">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative">
                    <div className="absolute -top-8 -right-8 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
                    <img
                      src={mode.image}
                      alt={mode.title}
                      className="relative rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
              <i className="ri-service-line text-blue-600"></i>
              <span className="text-sm text-blue-600 font-semibold">Additional Services</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Complete <strong>Logistics Support</strong>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              End-to-end services to ensure smooth and efficient international shipments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-18 h-18 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg" style={{ width: '4.5rem', height: '4.5rem' }}>
                  <i className={`${service.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
              <i className="ri-map-2-line text-blue-600"></i>
              <span className="text-sm text-blue-600 font-semibold">Service Coverage</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Extensive <strong>Network Coverage</strong>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We serve major cities and regions across Europe and Ukraine with reliable logistics solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {regions.map((region, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                    <i className="ri-map-pin-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">{region.name}</h3>
                </div>
                <div className="space-y-2">
                  {region.cities.map((city, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-gradient-to-br from-blue-600 to-cyan-500 rounded flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-slate-700">{city}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Interactive Route Map
            </h3>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Visualize our extensive logistics network connecting major European and Ukrainian cities.
            </p>
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5203874.003997!2d20.5!3d50.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDAwJzAwLjAiTiAyMMKwMzAnMDAuMCJF!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Logistics Network Map"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Need Logistics <strong>Support?</strong>
          </h2>
          <p className="text-xl text-slate-600 mb-12">
            Contact our logistics team to discuss your shipping requirements and get a customized solution.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-message-3-line mr-2"></i>
            Get Logistics Quote
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LogisticsPage;
